import { displayDates } from './auditFormat';

/**
 * Build the downloadable CSV from an audit result, in the browser.
 *
 * The server already returns every finding in `result.findings`, so the download
 * is assembled from what is on screen rather than asking for the audit again.
 * That keeps the button instant and means the file can never disagree with the
 * table the reviewer is looking at.
 */

// Column order of the findings export. Matches what the engine emits, so the
// sheet reads the same as the command-line report it replaces.
const FINDING_COLUMNS = [
  'Category', 'Scope', 'Person', 'Export Drug', 'MYM Drug', 'Prescription ID',
  'Field', 'Export Value', 'MYM Value', 'Note', 'CSV Line'
];

// Dates leave the engine as ISO; these sheets are read by people, so they carry
// the same MM/DD/YYYY the screen shows. See auditFormat.js.
const csvCell = value => {
  const s = displayDates(value === null || value === undefined ? '' : String(value));
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = rows => rows.map(r => r.map(csvCell).join(',')).join('\r\n') + '\r\n';

const slug = s => String(s || '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'facility';

/**
 * Both sheets report on THE UPLOADED FILE ONLY.
 *
 * EXTRA_IN_MYM findings - prescriptions active here that the export does not
 * mention - are dropped. They are a real observation, and they stay on screen,
 * but they answer a different question ("is our list longer than theirs?") than
 * the one these sheets are for ("is what they sent us right?"). A reviewer
 * working the downloaded sheet cannot act on a row that is not in the file in
 * front of them.
 */
const isAboutTheExport = finding => finding && finding.Category !== 'EXTRA_IN_MYM';

/**
 * How many findings the download will actually contain.
 *
 * The button's badge must count what lands in the file, not what the API
 * returned - those differ by every EXTRA_IN_MYM row, and on a facility where we
 * hold far more than the export lists that gap is most of the number.
 */
export const countExportedFindings = result =>
  ((result && result.findings) || []).filter(isAboutTheExport).length;

/** One row per field-level finding - the worklist. */
export const buildFindingsCsv = result => {
  const findings = ((result && result.findings) || []).filter(isAboutTheExport);
  return toCsv([FINDING_COLUMNS, ...findings.map(f => FINDING_COLUMNS.map(c => f[c]))]);
};

/**
 * The paired side-by-side sheet: each export row with ours directly beneath it,
 * and a Status line carrying the per-cell verdict. This is the layout a nurse
 * signs off against, so it keeps the on-screen column order.
 */
export const buildComparisonCsv = result => {
  if (!result) return '';
  const fields = (result.columns && result.columns.compared) || [];
  const head = ['#', 'Line', 'Person', 'Prescription ID', 'Source', ...fields.map(f => f.label)];
  const rows = [head];

  (result.pairs || []).forEach((pair, i) => {
    const n = i + 1;
    rows.push([n, pair.line, pair.person, pair.prescriptionId, 'Export',
      ...pair.cells.map(c => c.export)]);
    rows.push(['', '', '', '', 'MapYourMeds', ...pair.cells.map(c => c.mym)]);
    rows.push(['', '', '', pair.verdict, 'Status',
      ...pair.cells.map(c => (c.note ? `${c.status} - ${c.note}` : c.status))]);
  });

  if ((result.missing || []).length) {
    rows.push([]);
    rows.push(['Not found in MapYourMeds']);
    rows.push(['Line', 'Person', 'Drug', 'Reason']);
    result.missing.forEach(m => rows.push([m.line, m.person, m.drug, m.note]));
  }

  // No "active in MapYourMeds but absent from the export" section - see
  // isAboutTheExport above. This sheet covers the uploaded file only.

  if ((result.systemicGaps || []).length) {
    rows.push([]);
    rows.push(['Columns with no field in MapYourMeds']);
    rows.push(['Column', 'Why']);
    result.systemicGaps.forEach(g => rows.push([g.header || g.label, g.why]));
  }

  return toCsv(rows);
};

/** Trigger the browser download. */
export const downloadCsv = (contents, fileName) => {
  // Lead with a BOM so Excel opens it as UTF-8 - without it, resident names
  // carrying accents arrive mangled, which on a medication sheet reads as bad
  // data. Written as an escape, not a literal: an invisible character here is
  // one stray reformat away from vanishing unnoticed.
  const blob = new Blob([String.fromCharCode(0xfeff), contents], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const auditFileName = (result, kind) => {
  const facility = slug(result && result.facility && result.facility.name);
  const mode = result && result.strict ? 'strict' : 'clinical';
  const stamp = new Date().toISOString().slice(0, 10);
  return `medication-audit-${kind}-${facility}-${mode}-${stamp}.csv`;
};

export default { buildFindingsCsv, buildComparisonCsv, downloadCsv, auditFileName };
